import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Clock, Eye, Loader2, Presentation, Radio, Search, Unlock } from "lucide-react";
import FocusStage from "../FocusTech/FocusStage";
import { focusApi } from "../../services/focusApi";

const pending = (list) => (list || []).filter((focus) => focus.status === "pending");

const formatDay = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
    : "";
};

// Un Focus proposé : titre, résumé, auteur ; « Lancer » pour le secrétaire.
const FocusCard = ({ focus, canStart, onStart }) => (
  <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4">
    <div className="font-semibold text-slate-900">{focus.title}</div>
    {focus.summary && <p className="text-sm text-slate-500 line-clamp-2">{focus.summary}</p>}
    <div className="mt-auto flex items-center justify-between gap-2">
      <span className="text-xs text-slate-400">
        {focus.author_name} · {focus.slide_count} diapo{focus.slide_count > 1 ? "s" : ""}
        {focus.circle_name ? ` · ${focus.circle_name}` : ""}
        {focus.status === "presented" && focus.presented_at ? ` · présenté le ${formatDay(focus.presented_at)}` : ""}
      </span>
      {canStart && (
        <button type="button" disabled={!focus.slide_count} onClick={() => onStart(focus.id)}
                title={focus.slide_count ? "Le présenter à tous" : "Pas encore de diapo"}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
          <Presentation size={13} /> Lancer
        </button>
      )}
    </div>
  </div>
);

// Recherche parmi tous les Focus de l'équipe : à présenter, ou déjà présentés (pour y revenir).
const FocusBrowser = ({ onStart, excludeIds }) => {
  const [tab, setTab] = useState("pending");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    focusApi.list(tab)
      .then((data) => !cancelled && setItems(Array.isArray(data) ? data : []))
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const needle = query.trim().toLowerCase();
  const shown = (items || [])
    .filter((focus) => !excludeIds.has(String(focus.id)))
    .filter((focus) => !needle || [focus.title, focus.summary, focus.author_name, focus.circle_name]
      .some((field) => String(field || "").toLowerCase().includes(needle)));
  const tabClass = (key) => `rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`;

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-0.5 rounded-xl bg-slate-200/70 p-1">
          <button type="button" className={tabClass("pending")} onClick={() => setTab("pending")}>À présenter</button>
          <button type="button" className={tabClass("presented")} onClick={() => setTab("presented")}>Déjà présentés</button>
        </div>
        <label className="relative min-w-[200px] flex-1">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Titre, auteur, cercle…"
                 aria-label="Chercher un Focus tech"
                 className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-violet-300 focus:outline-none" />
        </label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!items && !error ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : shown.length === 0 ? (
        <p className="py-2 text-sm italic text-slate-400">
          {tab === "pending" ? "Aucun autre Focus à présenter." : "Aucun Focus déjà présenté."}
        </p>
      ) : (
        <div className="grid max-h-[420px] gap-3 overflow-y-auto pr-1 md:grid-cols-2">
          {shown.map((focus) => <FocusCard key={focus.id} focus={focus} canStart onStart={onStart} />)}
        </div>
      )}
    </div>
  );
};

/** Étape « Focus tech » sans présentation en cours : le secrétaire importe le Focus à présenter. */
const FocusPicker = ({ meetingFocusList, focusActions, userIsSecretary, onSelectMeetingPhase }) => {
  const suggested = pending(meetingFocusList);
  const doneHere = (meetingFocusList || []).filter((focus) => focus.presented_here);
  const [browsing, setBrowsing] = useState(false);
  const excludeIds = new Set(suggested.map((focus) => String(focus.id)));
  const start = (focusId) => focusActions && focusActions.start(focusId);
  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
      <div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Focus tech</p>
        <h3 className="text-xl font-bold text-slate-800 mt-1">Une présentation à l'équipe</h3>
        <p className="text-sm text-slate-500 mt-1">
          {userIsSecretary
            ? "Importe le Focus à présenter : il s'affiche chez tous et son auteur le fait défiler."
            : "Le secrétaire choisit le Focus à présenter ; il s'affichera ici."}
        </p>
      </div>

      {suggested.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Préparés pour cette réunion</p>
          <div className="grid gap-3 md:grid-cols-2">
            {suggested.map((focus) => (
              <FocusCard key={focus.id} focus={focus} canStart={userIsSecretary} onStart={start} />
            ))}
          </div>
        </div>
      )}

      {userIsSecretary && (
        browsing || suggested.length === 0 ? (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {suggested.length > 0 ? "Importer un autre Focus" : "Importer un Focus tech"}
            </p>
            <FocusBrowser onStart={start} excludeIds={excludeIds} />
          </div>
        ) : (
          <button type="button" onClick={() => setBrowsing(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-violet-200 hover:text-violet-700">
            <Search size={13} /> Importer un autre Focus (y compris déjà présentés)
          </button>
        )
      )}

      {!userIsSecretary && suggested.length === 0 && doneHere.length === 0 && (
        <p className="text-sm italic text-slate-400">Pas encore de Focus choisi.</p>
      )}

      {doneHere.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Présentés dans cette réunion</p>
          {doneHere.map((focus) => (
            <div key={focus.id} className="flex items-center gap-1.5 text-sm text-slate-500">
              <CheckCircle2 size={14} className="text-emerald-500" />
              <span className="truncate">{focus.title}</span>
              <span className="text-xs text-slate-400">· {focus.author_name}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <a href="/tools/focus-tech" target="_blank" rel="noreferrer" className="text-xs font-semibold text-slate-500 hover:text-indigo-700">
          Préparer un Focus (Outils → Focus tech, nouvel onglet)
        </a>
        {userIsSecretary && (
          <button type="button" onClick={() => onSelectMeetingPhase && onSelectMeetingPhase("agenda")}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">
            Passer à l'ordre du jour →
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Étape « Focus tech » de la tactique. Le présentateur (auteur du Focus) fait défiler ses diapos et ses gestes
 * sont rejoués chez les autres (voir backend focus_bridge.js) ; chacun peut décrocher pour explorer puis revenir.
 * Sans présentateur connecté, le secrétaire fait défiler à sa place.
 */
const FocusTechLive = ({ meetingFocus, meetingFocusList, focusActions, userIsSecretary, currentUserId,
                         connectedUsers = [], onSelectMeetingPhase }) => {
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState(null);
  const [exploring, setExploring] = useState(false);
  const [localIndex, setLocalIndex] = useState(0);
  const [epoch, setEpoch] = useState(0);
  const frameRef = useRef(null);

  const focusId = meetingFocus?.focus_id;
  const revision = meetingFocus?.revision;
  const liveSlide = meetingFocus?.slide || 0;
  const isPresenter = Boolean(meetingFocus) && String(meetingFocus.author_id) === String(currentUserId);
  const presenterHere = Boolean(meetingFocus) && connectedUsers.some((u) => String(u?.id) === String(meetingFocus.author_id));
  const canDrive = isPresenter || (userIsSecretary && !presenterHere);
  const following = !canDrive && !exploring;
  const index = exploring ? localIndex : liveSlide;
  const role = canDrive ? "presenter" : following ? "viewer" : "free";

  useEffect(() => {
    if (!focusId) {
      setDeck(null);
      setExploring(false);
      return undefined;
    }
    let cancelled = false;
    setError(null);
    focusApi.get(focusId)
      .then((data) => !cancelled && setDeck(data))
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [focusId, revision]);

  // Participant qui suit : les gestes du présentateur sont rejoués dans sa diapo.
  useEffect(() => {
    if (!following || !focusActions) return undefined;
    return focusActions.subscribe((message) => {
      if (String(message.from) === String(currentUserId)) return;
      if (message.type === "focus_events") frameRef.current?.replay(message.events);
      if (message.type === "focus_state") frameRef.current?.applyState(message.state);
    });
  }, [following, focusActions, currentUserId]);

  // Diapo prête : rattraper ce que le présentateur a déjà fait sur cette diapo (arrivée, rechargement, retour).
  const onReady = useCallback(() => {
    if (exploring || !focusActions) return;
    const snapshot = focusActions.snapshot();
    if (snapshot.slide === liveSlide && (snapshot.events.length || snapshot.state != null)) {
      frameRef.current?.catchUp(snapshot.events, snapshot.state);
    }
  }, [exploring, focusActions, liveSlide]);

  const slides = deck?.slides || [];
  const onIndexChange = canDrive
    ? (next) => focusActions.slide(next, slides.length)
    : exploring ? setLocalIndex : undefined;

  if (!meetingFocus) {
    return (
      <FocusPicker meetingFocusList={meetingFocusList} focusActions={focusActions} userIsSecretary={userIsSecretary}
                   onSelectMeetingPhase={onSelectMeetingPhase} />
    );
  }

  const button = "inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-indigo-200 hover:text-indigo-700";
  const notes = isPresenter ? slides[Math.min(index, slides.length - 1)]?.notes : null;
  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-bold text-violet-600 uppercase tracking-wider">
            <Radio size={12} /> Focus tech en cours
          </p>
          <h3 className="text-xl font-bold text-slate-800 mt-1">{meetingFocus.title}</h3>
          <p className="text-sm text-slate-500">
            Présenté par {isPresenter ? "toi" : meetingFocus.author_name}
            {canDrive && !isPresenter && " · absent : tu fais défiler à sa place"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(userIsSecretary || isPresenter) && (
            <button type="button" className={button} onClick={() => focusActions.end(true)}>
              <CheckCircle2 size={13} /> Terminer le Focus
            </button>
          )}
          {userIsSecretary && (
            <>
              <button type="button" className={button} onClick={() => focusActions.end(false)}
                      title="Arrêter sans le marquer présenté : il sera reproposé">
                <Clock size={13} /> Remettre à plus tard
              </button>
              <button type="button" onClick={() => onSelectMeetingPhase && onSelectMeetingPhase("agenda")}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
                Passer à l'ordre du jour
              </button>
            </>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!deck ? (
        <div className="flex aspect-video items-center justify-center rounded-lg bg-slate-50">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <FocusStage
          ref={frameRef}
          slides={slides}
          index={index}
          onIndexChange={onIndexChange}
          keyboard={canDrive || exploring}
          role={role}
          locked={following}
          frameKey={`${focusId}-${revision}-${exploring ? "free" : "live"}-${epoch}`}
          onEvents={canDrive ? (events) => focusActions.sendEvents(liveSlide, events) : undefined}
          onState={canDrive ? (state) => focusActions.sendState(liveSlide, state) : undefined}
          onReady={onReady}
          notice={exploring && localIndex !== liveSlide ? (
            <span className="text-xs font-semibold text-amber-600">Le présentateur est à la diapo {liveSlide + 1}</span>
          ) : null}
          toolbar={canDrive ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600">
              <Radio size={12} /> Tout le monde voit tes gestes
            </span>
          ) : exploring ? (
            <button type="button" className={button} onClick={() => { setExploring(false); setEpoch((n) => n + 1); }}>
              <Eye size={13} /> Revenir au direct
            </button>
          ) : (
            <button type="button" className={button} onClick={() => { setLocalIndex(liveSlide); setExploring(true); }}
                    title="Tourner la 3D, changer de diapo… sans gêner les autres">
              <Unlock size={13} /> Explorer librement
            </button>
          )}
        />
      )}
      {notes && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="font-semibold">Tes notes : </span>{notes}
        </p>
      )}
    </div>
  );
};

export default FocusTechLive;
